import {
  Alert,
  Box,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useScopeLabel } from '../people';
import { LOG_TIME_SCOPES, peopleWhoCanLogTime } from '../sampleData';
import type { LogTimeScope, ReachChange, SettingsSample } from '../sampleData';
import type { DetailsPatch, DetailsValues } from '../state';

import { FieldHelp } from './FormField';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { MonoText } from '@/components/ui';

/**
 * Who can log time, edited (brief §3, slice 4): v1's five levels, each with
 * how many people it reaches; the help (decision 11); the ETC switch; and,
 * while the level differs from what is saved, the consequence —
 * "10 → 200 people can log time · +190 gaining · −0 losing · 10 keeping ·
 * See who →". The numbers come from v1's `compareReach`, so they add up:
 * keeping + gaining = after, keeping + losing = before.
 *
 * At most one alert in the card (§3): the yellow warning, and only for
 * Everyone in the company. The consequence is a line, not an alert.
 */
export type LogTimeFormProps = {
  draft: Pick<DetailsValues, 'logTimeScope' | 'askForEtc' | 'legalEntityId' | 'departmentId'>;
  sample: SettingsSample;
  /** The saved level against the draft's — `null` while they are the same. */
  change: ReachChange | null;
  onChange: (patch: DetailsPatch) => void;
  onSeeWho: () => void;
};

/** The library's radios (P6/N7), as v1 settled: small, black-80, blue-80 checked. */
const RADIO_SX = {
  p: 0,
  mr: 1.5,
  color: 'var(--color-black-80)',
  '&.Mui-checked': { color: 'var(--color-blue-80)' },
} as const;

export const LogTimeForm = ({ draft, sample, change, onChange, onSeeWho }: LogTimeFormProps) => {
  const { t } = useTranslation('pmDetails');
  const scopeLabel = useScopeLabel(sample);
  const settingFor = (scope: LogTimeScope) => ({
    scope,
    legalEntityId: draft.legalEntityId,
    departmentId: draft.departmentId,
  });
  const companyWide = change !== null && draft.logTimeScope === 'all' && change.gaining.length > 0;

  return (
    <Stack sx={{ gap: 2 }}>
      <Box>
        <RadioGroup
          aria-label={t('logTime.Title')}
          value={draft.logTimeScope}
          onChange={(event) => onChange({ logTimeScope: event.target.value as LogTimeScope })}
          sx={{ gap: 0.25 }}
        >
          {LOG_TIME_SCOPES.map((scope) => (
            <FormControlLabel
              key={scope}
              value={scope}
              data-automation-id={`details-scope-row-${scope}`}
              sx={{
                mx: -1,
                px: 1,
                minHeight: 'var(--input-height-medium)',
                borderRadius: 'var(--border-radius-medium)',
                '&:hover': { bgcolor: 'var(--color-fog-10)' },
                '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
              }}
              control={
                <Radio
                  size="small"
                  sx={RADIO_SX}
                  slotProps={{
                    input: { 'data-automation-id': `details-scope-${scope}` } as object,
                  }}
                />
              }
              label={
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                  <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                    {scopeLabel(settingFor(scope))}
                  </Box>
                  <MonoText size="label" color="var(--color-fog-80)">
                    {String(peopleWhoCanLogTime(sample, settingFor(scope)).size)}
                  </MonoText>
                </Stack>
              }
            />
          ))}
        </RadioGroup>
        <Box sx={{ mt: 0.5 }} data-automation-id="details-log-time-help">
          <FieldHelp>
            {t('logTime.Help')} {t('logTime.HelpDraft')}
          </FieldHelp>
        </Box>
      </Box>

      <FormControlLabel
        sx={{ mx: 0, gap: 1, alignSelf: 'flex-start' }}
        control={
          <Switch
            checked={draft.askForEtc}
            onChange={(event) => onChange({ askForEtc: event.target.checked })}
            slotProps={{ input: { 'data-automation-id': 'details-ask-etc' } as object }}
          />
        }
        label={t('logTime.EtcToggle')}
      />

      {change ? (
        <Stack sx={{ gap: 1 }}>
          <Stack
            direction="row"
            data-automation-id="details-log-time-consequence"
            sx={{
              alignItems: 'center',
              flexWrap: 'wrap',
              columnGap: 2,
              rowGap: 0.5,
              px: 2,
              py: 1.25,
              bgcolor: 'var(--color-fog-10)',
              borderRadius: 'var(--border-radius-medium)',
            }}
          >
            <Typography
              variant="bodyBold"
              component="span"
              data-automation-id="details-consequence-total"
            >
              <Box component="span" sx={{ fontFamily: 'var(--font-family-mono)' }}>
                {change.before} → {change.after}
              </Box>{' '}
              {t('logTime.CanLogTime', { count: change.after })}
            </Typography>
            <MonoText
              size="label"
              color="var(--color-success-100)"
              data-automation-id="details-consequence-gaining"
            >
              {t('logTime.Gaining', { count: change.gaining.length })}
            </MonoText>
            <MonoText
              size="label"
              color="var(--color-error-100)"
              data-automation-id="details-consequence-losing"
            >
              {t('logTime.Losing', { count: change.losing.length })}
            </MonoText>
            <MonoText
              size="label"
              color="var(--color-fog-100)"
              data-automation-id="details-consequence-keeping"
            >
              {t('logTime.Keeping', { count: change.keeping.length })}
            </MonoText>
            <Link
              component="button"
              type="button"
              onClick={onSeeWho}
              sx={{
                ml: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                font: 'inherit',
                fontSize: 'var(--font-size-body-regular)',
              }}
              data-automation-id="details-see-who"
            >
              {t('logTime.SeeWho')}
              <MaterialSymbol name="arrow_forward" size={16} />
            </Link>
          </Stack>
          {companyWide ? (
            <Alert severity="warning" data-automation-id="details-log-time-warning">
              {t('logTime.Warning', { count: change.gaining.length })}
            </Alert>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
};
